import { all, call, put, takeLatest } from 'redux-saga/effects';
import axios from 'axios';

import {
  fetchPartiesSuccess,
  fetchPartiesFail,
  fetchPartySuccess,
  fetchPartyFail,
  fetchGendersSuccess,
  fetchGendersFail,
  updatePartyPersonSuccess,
  updatePartyPersonFail,
  updatePartyCompanySuccess,
  updatePartyCompanyFail,
  updatePartyAddressesSuccess,
  updatePartyAddressesFail,
  updatePartyContactsSuccess,
  updatePartyContactsFail,
  updatePartyBankAccountsSuccess,
  updatePartyBankAccountsFail,
  updatePartyRelationsSuccess,
  updatePartyRelationsFail,
  loadMorePartiesSuccess,
  loadMorePartiesFail
} from './actions';
import {
  FETCH_PARTIES_REQUEST,
  FETCH_PARTY_REQUEST,
  UPDATE_PARTY_PERSON_REQUEST,
  UPDATE_PARTY_COMPANY_REQUEST,
  UPDATE_PARTY_ADDRESSES_REQUEST,
  UPDATE_PARTY_CONTACTS_REQUEST,
  UPDATE_PARTY_BANK_ACCOUNTS_REQUEST,
  UPDATE_PARTY_RELATIONS_REQUEST,
  CHANGE_PAYMENT_CARD_REQUEST,
  LOAD_MORE_PARTIES_REQUEST
} from './actionTypes';
import { logout } from '../Auth/actions';
import { getOptions, processResponse } from '../../Utils/Utils';

const updatePartyPersonUrl = id => `/api/v1/party/person/${id}`;
const updatePartyCompanyUrl = id => `/api/v1/party/company/${id}`;
const updatePartyAddressesUrl = id => `/api/v2/parties/${id}/change_address`;
const updatePartyContactsUrl = id => `/api/v2/parties/${id}/contact/actions/update`;
const updatePartyBankAccountsUrl = id => `/api/v2/parties/${id}/change_bank_account`;
const updatePartyRelationsUrl = id => `/api/v1/party/person/${id}/relations`;
const changePaymentCardV2 = () => `/api/v2/parties/actions/request_print_change_payment_card`;
const fetchPartiesV2 = () => `/api/v2/graphql/b2b`

// Moved api call into own function (for easy test swapping)
export function fetchFromApi(options) {
  return axios(options)
    .then(response => ({ response }))
    .catch(error => ({ error }));
}

function* parties(firstName = '', name = '', birth_date = '', offset = 0, limit = 100) {
  let graphqlQuery = `query {
    parties(networkCode: "$networkCode", offset: $offset, limit: $limit, sortOrder: "desc"){
      id
      name
      code
      active
      birthDate
      name
      firstName
      isPerson
    }
  }`
  graphqlQuery = graphqlQuery.replace(/\$offset/, offset)

  graphqlQuery = graphqlQuery.replace(/\$limit/g, limit)
  const body = JSON.stringify({
    query: graphqlQuery,
    getEntitiesByNetwork: true
  })
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: fetchPartiesV2(), method: 'POST', data: body })
  );
  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success': {
      yield put(fetchPartiesSuccess({ data: data?.data, offset: limit }));
      break;
    }
    case 'fail':
      yield put(fetchPartiesFail({ error: alerts }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
      break;
  }
  return { response, error };
}

// PARTY
function* party({ id }) {
  let graphqlQuery = `query {
    parties(networkCode: "$networkCode", code: "$code"){
      id
      name
      code
      active
      birthDate
      name
      firstName
      isPerson
      gender {
        prefix
      }
      address {
        id
        fullAddress
        zip
        city
        country {
          name
        }
      }
      addresses {
        id
        fullAddress
        zip
        city
        country {
          name
        }
      }
      bankAccounts {
        number
        bank {
          bic
        }
        id
      }
      relations {
        id
        type {
          name
          code
        }
        to {
          id
        }
      }
      contracts {
        id
        contractStatus
        contractNumber
        startDate
        subscriber {
          id
          name
          code
        }
      }
      email
      mobile
      phone
    }
  }`
  graphqlQuery = graphqlQuery.replace(/\$code/, id)  // TODO: extract code instead of id or support search by code in graphql api

  const body = JSON.stringify({
    query: graphqlQuery,
    getEntitiesByNetwork: true
  })

  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: fetchPartiesV2(), method: 'POST', data: body })
  );
  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success': {
      yield put(fetchPartySuccess({ data: data?.data, id }));
      break;
    }
    case 'fail':
      yield put(fetchPartyFail({ error: alerts, id }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
      break;
  }
  return { response, error };
}

// PUT PARTY PERSON
function* updatePartyPerson({ id, payload }) {
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: updatePartyPersonUrl(id), method: 'PUT', data: payload })
  );
  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success':
      yield put(updatePartyPersonSuccess({ data, id }));
      break;
    case 'fail':
      yield put(updatePartyPersonFail({ error: alerts, id }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
      break;
  }
}

// PUT PARTY PERSON
function* updatePartyCompany({ id, payload }) {
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: updatePartyCompanyUrl(id), method: 'PUT', data: payload })
  );
  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success':
      yield put(updatePartyCompanySuccess({ data }));
      break;
    case 'fail':
      yield put(updatePartyCompanyFail({ error: alerts, id }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
      break;
  }
}

// PUT PARTY ADDRESSES
function* updatePartyAddresses({ id, payload }) {
  const newAddresses = []
  for (let address of payload) {
    let newAddress = {
      'street': address.street,
      'zip': address.zip,
      'city': address.city,
      'country': 'fr' // TODO: make it dynamic: address.country (need to add field in form for input)
    }
    newAddresses.push(newAddress)
  }
  const dataV2 = { 'party': { 'id': id }, 'new_addresses': newAddresses }
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: updatePartyAddressesUrl(id), method: 'POST', data: dataV2 })
  );
  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success':
      yield put(updatePartyAddressesSuccess({ data }));
      break;
    case 'fail':
      yield put(updatePartyAddressesFail({ error: alerts, id }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
      break;
  }
}

// PUT PARTY CONTACTS
function* updatePartyContacts({ id, payload }) {
  const newContactMechanisms = []
  for (let contactMechnism of payload) {
    let type = contactMechnism.type ? contactMechnism.type : 'phone'
    let newContactMechanism = {
      'type': type,
      'values': [contactMechnism.value]
    }
    if (newContactMechanisms.length === 0) {
      newContactMechanisms.push(newContactMechanism)
    } else {
      for (let cM of newContactMechanisms) {
        if (cM.type === type) {
          cM.values.push(contactMechnism.value)
        }
      }
    }
  }
  const dataV2 = { 'party': { 'id': id }, 'new_contact_mechanisms': newContactMechanisms }
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: updatePartyContactsUrl(id), method: 'POST', data: dataV2 })
  );
  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success':
      yield put(updatePartyContactsSuccess({ data }));
      break;
    case 'fail':
      yield put(updatePartyContactsFail({ error: alerts, id }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
      break;
  }
}

// PUT PARTY BANK ACCOUNTS
function* updatePartyBankAccounts({ id, payload }) {
  // TODO: not available yet, need to add form for this update
  const newBankAccounts = []
  for (let bankAccount of payload) {
    let newBankAccount = {
      'bank': {
        'bic': bankAccount.bic
      },
      'number': bankAccount.number
    }
    newBankAccounts.push(newBankAccount)
  }
  const dataV2 = { 'party': { 'id': id }, 'new_accounts': newBankAccounts }
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: updatePartyBankAccountsUrl(id), method: 'POST', data: dataV2 })
  );
  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success':
      yield put(updatePartyBankAccountsSuccess({ data }));
      break;
    case 'fail':
      yield put(updatePartyBankAccountsFail({ error: alerts, id }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
      break;
  }
}

// PUT PARTY RELATIONS
function* updatePartyRelations({ id, payload }) {
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: updatePartyRelationsUrl(id), method: 'PUT', data: payload })
  );
  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success':
      yield put(updatePartyRelationsSuccess({ data }));
      break;
    case 'fail':
      yield put(updatePartyRelationsFail({ error: alerts, id }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
      break;
  }
}

// CHANGE PAYMENT CARD
function* changePaymentCard({ payload }) {
  let contractIds = []
  for (let contract of payload) {
    contractIds.push({ 'id': parseInt(contract.id, 10) })
  }
  const dataV2 = {
    'contracts': contractIds
  }
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: changePaymentCardV2(), method: 'POST', data: dataV2 })
  );
  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success':
      yield put(updatePartyRelationsSuccess({ data }));
      break;
    case 'fail':
      yield put(updatePartyRelationsFail({ error: alerts }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
      break;
  }
}

// GET GENDERS
function* genders() {
  // const { response, error } = yield call(fetchFromApi, getOptions({ url: gendersUrl() }));
  // NO v2 EQUIVALENT => HARD CODE RESPONSE
  const response = {
    "context": "{}",
    "loading": "eager",
    "name": "gender",
    "depends": [
        "gender",
        "active",
        "is_person"
    ],
    "on_change": [
        "gender",
        "id"
    ],
    "on_change_with": [],
    "readonly": false,
    "required": false,
    "states": "{\"invisible\": {\"__class__\": \"Not\", \"v\": {\"__class__\": \"Bool\", \"v\": {\"__class__\": \"Eval\", \"v\": \"is_person\", \"d\": \"\"}}}, \"required\": {\"__class__\": \"Bool\", \"v\": {\"__class__\": \"Eval\", \"v\": \"is_person\", \"d\": \"\"}}, \"readonly\": {\"__class__\": \"Or\", \"s\": [{\"__class__\": \"Not\", \"v\": {\"__class__\": \"Eval\", \"v\": \"active\", \"d\": true}}, {\"__class__\": \"Not\", \"v\": {\"__class__\": \"Eval\", \"v\": \"active\", \"d\": true}}]}}",
    "type": "selection",
    "domain": "[]",
    "searchable": true,
    "sortable": true,
    "string": "Genre",
    "help": "",
    "selection": [
        [
            "male",
            "M."
        ],
        [
            "female",
            "Mme"
        ],
        [
            "",
            ""
        ]
    ],
    "selection_change_with": [],
    "sort": true,
    "create": true,
    "delete": true
  }
  const { type, data, alerts } = processResponse({ response });
  switch (type) {
    case 'success':
      yield put(fetchGendersSuccess({ data }));
      break;
    case 'fail':
      yield put(fetchGendersFail({ error: alerts }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
      break;
  }
}

function* loadMoreParties({ firstName = '', name = '', birth_date = '', offset = 0, limit = 100 }) {
  let graphqlQuery = `query {
    parties(networkCode: "$networkCode", offset: $offset, limit: $limit, sortOrder: "desc"){
      id
      name
      code
      active
      birthDate
      name
      firstName
      isPerson
    }
  }`
  graphqlQuery = graphqlQuery.replace(/\$offset/, offset)

  graphqlQuery = graphqlQuery.replace(/\$limit/g, limit)
  const body = JSON.stringify({
    query: graphqlQuery,
    getEntitiesByNetwork: true
  })
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({ url: fetchPartiesV2(), method: 'POST', data: body })
  );
  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success': {
      const newOffset = offset + limit;
      yield put(loadMorePartiesSuccess({ data: data?.data, offset: newOffset }));
      break;
    }
    case 'fail':
      yield put(loadMorePartiesFail({ error: alerts }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
      break;
  }
  return { response, error };
}

function* watchParties() {
  yield all([
    yield takeLatest(FETCH_PARTIES_REQUEST, parties),
    yield takeLatest(FETCH_PARTY_REQUEST, party),
    yield takeLatest(UPDATE_PARTY_PERSON_REQUEST, updatePartyPerson),
    yield takeLatest(UPDATE_PARTY_COMPANY_REQUEST, updatePartyCompany),
    yield takeLatest(UPDATE_PARTY_ADDRESSES_REQUEST, updatePartyAddresses),
    yield takeLatest(UPDATE_PARTY_CONTACTS_REQUEST, updatePartyContacts),
    yield takeLatest(UPDATE_PARTY_BANK_ACCOUNTS_REQUEST, updatePartyBankAccounts),
    yield takeLatest(UPDATE_PARTY_RELATIONS_REQUEST, updatePartyRelations),
    yield takeLatest(CHANGE_PAYMENT_CARD_REQUEST, changePaymentCard),
    yield takeLatest(LOAD_MORE_PARTIES_REQUEST, loadMoreParties)
  ]);
}

export default watchParties;
